import {FC, useEffect, useState} from "react";
import {useFormik} from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Link,
  CircularProgress,
  InputAdornment,
  IconButton,
  Avatar,
} from "@mui/material";
import {useLoginMutation} from "../../app/services/auth";
import {toast} from "react-toastify";
import {useTranslation} from "react-i18next";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {ALLOWED_PARENT_ORIGINS} from "../../util/helpers";
import {LoginResolutionPolicy} from "../../constants/enums";
import {getIdentityErrorMessage} from "../../util/identityErrorMapper";

export const GenericClassicLogin: FC = () => {
  const {t} = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [preLoginToken, setPreLoginToken] = useState<string>("");
  const [hostOrigin, setHostOrigin] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState<boolean>(false);

  const handleShowPasswordChange = () => setShowPassword(!showPassword);

  // Validation schema
  const validationSchema = Yup.object({
    dealerNumber: Yup.string().required(
      t("classic_login.validation.dealerNumberRequired"),
    ),
    username: Yup.string().required(
      t("classic_login.validation.userNameRequired"),
    ),
    password: Yup.string().required(
      t("classic_login.validation.passwordRequired"),
    ),
  });

  const [login, {isLoading}] = useLoginMutation();

  //* Validate hostOrigin from URL parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hostOriginParam = params.get("hostOrigin");

    if (hostOriginParam && ALLOWED_PARENT_ORIGINS.includes(hostOriginParam)) {
      setHostOrigin(hostOriginParam);
    } else {
      console.error("Untrusted hostOrigin:", hostOriginParam);
    }
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (hostOrigin) {
        const allowedOrigin = new URL(hostOrigin).origin;

        // Only accept messages from the parent (ClientApp), not self (IDMS)
        if (event.origin !== allowedOrigin) {
          console.debug(
            "Ignored message from unexpected origin:",
            event.origin,
          );
          return;
        }
      }

      if (event.data?.type === "PRE_LOGIN_TOKEN") {
        const token = event.data.token;
        setPreLoginToken(token);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [hostOrigin]);

  useEffect(() => {
    if (hostOrigin) {
      window.parent.postMessage({type: "REQUEST_PRE_LOGIN_TOKEN"}, hostOrigin);
    }
  }, [hostOrigin]);

  const formik = useFormik({
    initialValues: {
      cacheKey: preLoginToken,
      dealerNumber: "",
      username: "",
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: async values => {
      const loginReqest: GenericClassicLoginRequest = {
        dealerCacheKey: preLoginToken,
        dealerNumber: values.dealerNumber,
        username: values.username,
        password: values.password,
        loginResolutionPolicy: LoginResolutionPolicy.CacheKeyDealerId,
      };

      await login(loginReqest)
        .unwrap()
        .then(response => {
          toast.success(t("classic_login.messages.loginSuccess"));

          // Send token to host page (Client App)
          if (hostOrigin) {
            window.parent.postMessage(
              {
                type: "LOGIN_SUCCESS",
                access_token: response.access_token,
                refresh_token: response.refresh_token,
                expires_in: response.expires_in,
                token_type: response.token_type,
                scope: response.scope,
              },
              hostOrigin,
            );
          }
        })
        .catch(error => {
          console.error("Login failed", error);
          toast.error(getIdentityErrorMessage(error, t));
        });
    },
  });

  // Remember dealer number checkbox handler
  const handleCheckboxChange = (isCheck: boolean) => {
    if (isCheck) {
      if (formik.values.dealerNumber.length > 0) {
        window.localStorage.setItem(
          "X_Login_RememberMe_info_X",
          JSON.stringify({
            isCheck: true,
            dealerNumber: formik.values.dealerNumber,
          }),
        );
        setRememberMe(true);
      } else {
        window.localStorage.removeItem("X_Login_RememberMe_info_X");
        setRememberMe(false);
      }
    } else {
      window.localStorage.removeItem("X_Login_RememberMe_info_X");
      setRememberMe(false);
    }
  };

  // On mount, load remembered dealer number if present
  useEffect(() => {
    const loginInfo = window.localStorage.getItem("X_Login_RememberMe_info_X");
    if (loginInfo != null) {
      const values = JSON.parse(loginInfo);
      if (values.isCheck) {
        setRememberMe(true);
        formik.setFieldValue("dealerNumber", values.dealerNumber);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When dealerNumber changes and rememberMe is checked, update localStorage
  useEffect(() => {
    if (rememberMe) {
      if (formik.values.dealerNumber.length > 0) {
        window.localStorage.setItem(
          "X_Login_RememberMe_info_X",
          JSON.stringify({
            isCheck: true,
            dealerNumber: formik.values.dealerNumber,
          }),
        );
      } else {
        window.localStorage.removeItem("X_Login_RememberMe_info_X");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.dealerNumber]);

  return (
      <Container
        component="main"
        maxWidth="xs"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backgroundColor: "background.paper",
            padding: 4,
            borderRadius: 2,
            boxShadow: 3,
          }}>
          <Avatar sx={{bgcolor: "primary.main"}}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5" sx={{mb: 3}}>
            {t("classic_login.title")}
          </Typography>
          <form onSubmit={formik.handleSubmit}>
            <TextField
              size="small"
              variant="outlined"
              margin="dense"
              fullWidth
              label={t("classic_login.dealerNumberLabel")}
              name="dealerNumber"
              autoFocus
              value={formik.values.dealerNumber}
              onChange={formik.handleChange}
              error={
                formik.touched.dealerNumber &&
                Boolean(formik.errors.dealerNumber)
              }
              helperText={
                formik.touched.dealerNumber && formik.errors.dealerNumber
              }
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                onKeyPress: (event: React.KeyboardEvent<HTMLInputElement>) => {
                  const keyCode = event.keyCode || event.which;
                  const keyValue = String.fromCharCode(keyCode);
                  if (!/^\d+$/.test(keyValue)) {
                    event.preventDefault();
                  }
                },
              }}
            />
            <TextField
              size="small"
              variant="outlined"
              margin="dense"
              fullWidth
              label={t("classic_login.usernameLabel")}
              name="username"
              value={formik.values.username}
              onChange={formik.handleChange}
              error={formik.touched.username && Boolean(formik.errors.username)}
              helperText={formik.touched.username && formik.errors.username}
            />

            <TextField
              size="small"
              variant="outlined"
              margin="dense"
              fullWidth
              name="password"
              label={t("classic_login.passwordLabel")}
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      id="passwordVisibilityBtn"
                      onClick={handleShowPasswordChange}>
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              style={{display: "flex"}}
              label={"Remember dealer number"}
              control={
                <Checkbox
                  size="medium"
                  color="primary"
                  checked={rememberMe}
                  onChange={e => handleCheckboxChange(e.target.checked)}
                />
              }
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{my: 2}}
              disabled={isLoading}>
              {t("classic_login.loginButton")}
              {isLoading && (
                <CircularProgress
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: -12,
                    marginLeft: -12,
                    color: "secondary",
                  }}
                  size={24}
                />
              )}
            </Button>
          </form>
          <Link
            href="#"
            variant="body2"
            onClick={e => {
              e.preventDefault();
              if (hostOrigin) {
                window.parent.postMessage("USE_EMAIL_LOGIN", hostOrigin);
              }
            }}>
            {t("classic_login.classicLogin")}
          </Link>
        </Box>
      </Container>
  );
};
